<template>
  <div class="page">
    <div class="seiten-kopf">
      <router-link to="/einkaufsliste/zutaten-anzeigen" class="seiten-kopf-zurueck" aria-label="Zurück">
        <font-awesome-icon icon="fa-solid fa-arrow-left" />
      </router-link>
      <h1>Duplikate finden</h1>
    </div>

    <p v-if="Status" class="hint-message success">{{ Status }}</p>
    <p v-if="Fehlermeldung" class="error-message">{{ Fehlermeldung }}</p>

    <p class="hint-message">
      Findet exakte und ähnliche Namen im Zutaten-Katalog, sowie Zutaten mit gleichem Wortlaut
      (z.B. „Öl" und „Rapsöl"). Beim Zusammenführen werden alle Verwendungen in Rezepten und auf
      Einkaufslisten auf die ausgewählte Zutat übertragen – es geht nichts verloren.
    </p>

    <p v-if="Gruppen.length === 0" class="hint-message">Keine möglichen Duplikate gefunden.</p>

    <div v-else class="gruppen-liste">
      <div v-for="Gruppe in Gruppen" :key="Gruppe.Schluessel" class="gruppen-karte">
        <div class="gruppen-kopf">
          <span class="gruppen-titel">{{ Gruppe.Mitglieder.map(m => m.Name).join(' / ') }}</span>
          <span class="aehnlichkeit-badge">
            {{ Gruppe.NurTeilstring ? 'Enthält gleichen Namen' : Math.round(Gruppe.Score * 100) + '% ähnlich' }}
          </span>
        </div>

        <label
          v-for="Mitglied in Gruppe.Mitglieder"
          :key="Mitglied.Id"
          class="mitglied-zeile"
          :class="{ 'mitglied-zeile--ausgewaehlt': Gruppe.BehaltenId === Mitglied.Id }"
        >
          <input
            type="radio"
            :name="'gruppe-' + Gruppe.Schluessel"
            :checked="Gruppe.BehaltenId === Mitglied.Id"
            @change="BehaltenWaehlen(Gruppe.Schluessel, Mitglied.Id)"
          />
          <img
            v-if="Mitglied.HatBild"
            :src="ZutatBildUrl(Mitglied)"
            class="mini-bild"
            alt=""
            loading="lazy"
            @error="$event.target.style.display = 'none'"
          />
          <span class="mitglied-info">
            <span class="mitglied-name">{{ Mitglied.Name }}</span>
            <span class="mitglied-details">{{ KategorieName(Mitglied.KategorieId) }} · in {{ VerwendungsAnzahl(Mitglied.Id) }} Verwendung(en)</span>
          </span>
        </label>

        <button
          type="button"
          class="btn btn-primary btn-breit"
          :disabled="LaedtSchluessel === Gruppe.Schluessel"
          @click="Zusammenfuehren(Gruppe)"
        >
          <span class="btn-inhalt">
            <font-awesome-icon icon="fa-solid fa-code-merge" />
            Zusammenführen
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Zutaten,
  ZutatenLaden,
  Kategorien,
  KategorienLaden,
  KategorieFinden,
  Einkaufslistenpositionen,
  EinkaufslisteLaden,
  ZutatenZusammenfuehren
} from '../composables/useZutaten'
import { Rezepte, RezepteLaden } from '../composables/useRezepte'
import { DuplikatGruppenFinden } from '../composables/useDuplikate'
import { BildUrl } from '../composables/useBilder'

export default {
  name: 'ZutatenDuplikateView',
  data() {
    return {
      Zutaten,
      Kategorien,
      Rezepte,
      Einkaufslistenpositionen,
      Status: '',
      Fehlermeldung: '',
      Uebersteuerungen: {},
      LaedtSchluessel: null
    }
  },
  async created() {
    await Promise.all([ZutatenLaden(), KategorienLaden(), EinkaufslisteLaden(), RezepteLaden()])
  },
  computed: {
    Gruppen() {
      return DuplikatGruppenFinden(this.Zutaten).map(Gruppe => {
        const Schluessel = Gruppe.Mitglieder.map(m => m.Id).sort((a, b) => a - b).join('-')
        const Standard = this.StandardBehaltenId(Gruppe.Mitglieder)
        return {
          ...Gruppe,
          Schluessel,
          BehaltenId: this.Uebersteuerungen[Schluessel] ?? Standard
        }
      })
    }
  },
  methods: {
    VerwendungsAnzahl(ZutatId) {
      const RezeptAnzahl = this.Rezepte.filter(r => (r.Zutaten || []).some(z => z.ZutatId === ZutatId)).length
      const ListenAnzahl = this.Einkaufslistenpositionen.filter(e => e.ZutatId === ZutatId).length
      return RezeptAnzahl + ListenAnzahl
    },
    StandardBehaltenId(Mitglieder) {
      // Bevorzugt: hat ein Foto > mehr Verwendungen (Rezepte + Listen) > niedrigere Id (älter)
      return Mitglieder.slice().sort((a, b) => {
        const FotoA = a.HatBild ? 1 : 0
        const FotoB = b.HatBild ? 1 : 0
        if (FotoA !== FotoB) return FotoB - FotoA
        const VerwA = this.VerwendungsAnzahl(a.Id)
        const VerwB = this.VerwendungsAnzahl(b.Id)
        if (VerwA !== VerwB) return VerwB - VerwA
        return a.Id - b.Id
      })[0].Id
    },
    BehaltenWaehlen(Schluessel, Id) {
      this.Uebersteuerungen[Schluessel] = Id
    },
    KategorieName(KategorieId) {
      if (!KategorieId) return 'Ohne Kategorie'
      return KategorieFinden(KategorieId)?.Name ?? 'Ohne Kategorie'
    },
    ZutatBildUrl(Zutat) {
      return BildUrl('Zutat', Zutat.Id, Zutat.BildVersion)
    },
    async Zusammenfuehren(Gruppe) {
      this.Fehlermeldung = ''
      const Behalten = Gruppe.Mitglieder.find(m => m.Id === Gruppe.BehaltenId)
      const Andere = Gruppe.Mitglieder.filter(m => m.Id !== Gruppe.BehaltenId)
      const AndereNamen = Andere.map(m => `„${m.Name}“`).join(', ')
      const Frage = `${AndereNamen} mit „${Behalten.Name}“ zusammenführen? Alle Verwendungen in Rezepten und Einkaufslisten werden übertragen, ${Andere.length === 1 ? 'die andere Zutat wird' : 'die anderen Zutaten werden'} danach aus dem Katalog entfernt.`
      if (!confirm(Frage)) return

      this.LaedtSchluessel = Gruppe.Schluessel
      try {
        await ZutatenZusammenfuehren(Gruppe.BehaltenId, Andere.map(m => m.Id))
        await RezepteLaden(true)
        this.Status = `„${Behalten.Name}“ zusammengeführt.`
        setTimeout(() => { this.Status = '' }, 2500)
      } catch (Fehler) {
        this.Fehlermeldung = Fehler.message
      } finally {
        this.LaedtSchluessel = null
      }
    }
  }
}
</script>

<style scoped>
.gruppen-liste {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.gruppen-karte {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-lg);
  background: var(--glas);
}

.gruppen-kopf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.gruppen-titel {
  font-weight: 700;
  font-size: 1rem;
}

.aehnlichkeit-badge {
  flex-shrink: 0;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--color-accent-light);
  color: var(--color-accent);
  font-size: 0.72rem;
  font-weight: 700;
}

.mitglied-zeile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1.5px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.mitglied-zeile:hover {
  border-color: var(--color-primary);
}

.mitglied-zeile--ausgewaehlt {
  border-color: var(--color-primary);
  background: color-mix(in srgb, var(--color-primary) 8%, var(--color-surface));
}

.mitglied-zeile input[type="radio"] {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: var(--color-primary);
}

.mitglied-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.mitglied-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.mitglied-details {
  font-size: 0.78rem;
  color: var(--color-text-muted);
}

.btn-breit {
  width: 100%;
}
</style>
